/* eslint-env browser, es6 */
import m from "mithril";

import { KEY_ENTER } from "../constants";
import { cssTimeToMillis, getCssVariable } from "../utils";
import { validateNickname } from "../validators";

interface InputViewAttrs {
  onInputHook?: (value: string) => void;
  onEnterHook?: () => number;
  disabled: boolean;
}

const SCORE_BADGE_TRANSITION_TIME_MS = cssTimeToMillis(
  getCssVariable("--score-badge-transition-time")
);

export class InputView implements m.Component<InputViewAttrs> {
  private onInputHook?: (value: string) => void;
  private onEnterHook?: () => number;

  private scoreBadgeShowing: boolean;
  private scoreBadgePoints: number;

  constructor(vnode: m.Vnode<InputViewAttrs>) {
    this.onInputHook = vnode.attrs.onInputHook;
    this.onEnterHook = vnode.attrs.onEnterHook;

    this.scoreBadgeShowing = false;
    this.scoreBadgePoints = 0;
  }

  onInput(event: InputEvent): void {
    if (this.onInputHook) {
      this.onInputHook((<HTMLInputElement>event.target).value.trim());
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === KEY_ENTER) {
      if (!this.onEnterHook) {
        return;
      }

      const score = this.onEnterHook();
      if (score > 0) {
        this.scoreBadgeShowing = true;
        this.scoreBadgePoints = score;
        setTimeout(() => {
          this.scoreBadgeShowing = false;
          m.redraw();
        }, SCORE_BADGE_TRANSITION_TIME_MS);
      }
      const target = <HTMLInputElement>event.target;
      target.value = "";
    }
  }

  view(vnode: m.Vnode<InputViewAttrs>): m.Children {
    const scoreBadgeProps = this.scoreBadgeShowing
      ? { style: { opacity: "100%" } }
      : { style: { opacity: "0%" } };

    return m("span.input-box", [
      m("input.word-input", {
        disabled: vnode.attrs.disabled,
        oninput: (event: InputEvent) => this.onInput(event),
        onkeydown: (event: KeyboardEvent) => this.onKeyDown(event),
      }),
      m("span.score-badge", scoreBadgeProps, `+${this.scoreBadgePoints}`),
    ]);
  }
}

interface NicknameInputViewAttrs {
  hookOnInput: (value: string, isValid: boolean) => void;
  initialValue: string;
}

export class NicknameInputView implements m.Component<NicknameInputViewAttrs> {
  INPUT_ID: "input-nickname";

  private hookOnInput: (value: string, isValid: boolean) => void;
  private value: string;
  private validationError: string;

  constructor(vnode: m.Vnode<NicknameInputViewAttrs>) {
    this.hookOnInput = vnode.attrs.hookOnInput;
    this.value = vnode.attrs.initialValue;
    this.validationError = "";
  }

  oncreate(): void {
    (<HTMLInputElement>document.getElementById(this.INPUT_ID)).focus();
  }

  view(): m.Children {
    return [
      m("p.nickname", [
        m("label", { for: this.INPUT_ID }, "Choose a nickname:"),
      ]),
      m("input.nickname#" + this.INPUT_ID, {
        value: this.value,
        oninput: (event: InputEvent) => {
          this.value = (<HTMLInputElement>event.target).value;
          this.validationError = validateNickname(this.value);
          this.hookOnInput(this.value, !this.validationError);
        },
      }),
      m(
        "p.nickname-hint",
        "This will be visible to everyone who accepts the duel. "
      ),
      this.validationError ? m("p.nickname-error", this.validationError) : null,
    ];
  }
}
